import { useWatchProfile } from '@/hooks/useWatchProfile'
import { mentorshipsRepository, profileAwariRepository } from '@/repositories'
import { modalCreators } from '@/store/ducks/modal'
import { notificationCreators } from '@/store/ducks/notification'
import { useAppDispatch } from '@/store/hooks'
import { MentorshipsModel } from '@models'
import { useEffect } from 'react'
import profileAwariService from './profileAwariService'

function useListMentorships() {
	const { data, isLoading, isSuccess, refetch } =
		mentorshipsRepository.useListQuery(undefined, {
			refetchOnMountOrArgChange: true,
		})

	useEffect(() => {
		let intervalId: string | number | NodeJS.Timeout | undefined

		if (data && data.mentorships?.length > 0) {
			intervalId = setInterval(() => {
				refetch()
			}, 60000) // Polling a cada 60 segundos
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.mentorships?.length])

	return { data, isSuccess, isLoading, refetch }
}

function useListMentorshipsMutation() {
	const [
		_listMentorshipsMutation,
		{ data, isLoading, isSuccess, isError, error },
	] = mentorshipsRepository.useListMutationMutation()

	async function execute() {
		try {
			await _listMentorshipsMutation()
		} catch (error) {
			console.error(error)
		}
	}

	return { execute, data, isLoading, isSuccess, isError, error }
}

function useCreateMutation() {
	const { data: dataProfileAwari } = profileAwariService.getProfileAwari()

	const dispatch = useAppDispatch()

	const { refetch } = useWatchProfile()

	const [_createMentorship, { data, isLoading, isSuccess, isError, error }] =
		mentorshipsRepository.useScheduleMentorshipMutation()

	const userIsAwari = !!dataProfileAwari

	async function execute(body: MentorshipsModel.Create) {
		try {
			await _createMentorship({
				onboarding: false,
				userIsAwari: userIsAwari || false,
				...body,
			}).then(() => {
				dispatch(modalCreators.closeModal())
			})
		} catch (error) {
			console.error(error)
		}
	}

	useEffect(() => {
		if (isError && error) {
			let errorData =
				'data' in error ? (error.data as { message: string }) : null

			if (errorData?.message === 'insufficient limit empreenda') {
				dispatch(
					modalCreators.openModal({
						name: 'statusModal',
						type: 'error',
						modalInfo: {
							title: 'Limite atingido',
							buttonLabel: 'Fechar',
							message:
								'Limite de créditos para mentoria Empreenda foi atingido.',
						},
					})
				)
				return
			}

			if (errorData?.message === 'insufficient credits') {
				dispatch(
					modalCreators.openModal({
						name: 'statusModal',
						type: 'error',
						modalInfo: {
							title: 'Saldo insuficiente',
							buttonLabel: 'Fechar',
							message:
								'Você precisa de créditos para agendar essa sessão de mentoria. Torne-se membro da Awari para receber créditos e agendar sessões de mentoria individuais',
						},
					})
				)
				return
			}

			if (errorData?.message === 'slot is no longer available') {
				dispatch(
					modalCreators.openModal({
						name: 'statusModal',
						type: 'error',
						modalInfo: {
							title: 'Horário indisponível',
							buttonLabel: 'Atualizar página',
							message:
								'Esse horário não está mais disponível, por favor, atualize a página e selecione outro.',
						},
					})
				)
				return
			}

			dispatch(
				modalCreators.openModal({
					name: 'statusModal',
					type: 'error',
				})
			)
		}
	}, [isError, error, dispatch])

	useEffect(() => {
		if (isSuccess) refetch()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess])

	return { execute, data, isLoading, isSuccess, isError, error }
}

function useCancelMutation() {
	const dispatch = useAppDispatch()

	const [_cancelMentorship, { isLoading, isSuccess }] =
		mentorshipsRepository.useCancelMentorshipMutation()

	const { refetch } = useWatchProfile()

	async function execute(body: {
		mentorshipId: string
		typeReason: string
		reason?: string
	}) {
		try {
			await _cancelMentorship(body).then(() => {
				refetch()
				dispatch(modalCreators.closeModal())
				dispatch(
					notificationCreators.show({
						message: 'Mentoria cancelada',
					})
				)
			})
			await dispatch(profileAwariRepository.util.invalidateTags(['Profile']))
		} catch (error) {
			console.error(error)
		}
	}

	return { execute, isLoading, isSuccess }
}

function useRescheduleMutation() {
	const dispatch = useAppDispatch()

	const [
		_rescheduleMentorship,
		{ data, isLoading, isSuccess, isError, error },
	] = mentorshipsRepository.useRescheduleMentorshipMutation()

	async function execute(body: { mentorshipId: string; date: string }) {
		try {
			await _rescheduleMentorship({
				...body,
			}).then(() => {
				dispatch(modalCreators.closeModal())
			})
		} catch (error) {
			console.error(error)
		}
	}

	useEffect(() => {
		if (isError && error) {
			let errorData =
				'data' in error ? (error.data as { message: string }) : null

			if (errorData?.message === 'slot is no longer available') {
				dispatch(
					modalCreators.openModal({
						name: 'statusModal',
						type: 'error',
						modalInfo: {
							title: 'Horário indisponível',
							buttonLabel: 'Atualizar página',
							message:
								'Esse horário não está mais disponível, por favor, atualize a página e selecione outro.',
						},
					})
				)
				return
			}

			dispatch(
				modalCreators.openModal({
					name: 'statusModal',
					type: 'error',
				})
			)
		}
	}, [isError, error, dispatch])

	return { execute, data, isLoading, isSuccess, isError, error }
}

function useAttendMutation() {
	const [_attendMentorship, { isLoading, isSuccess }] =
		mentorshipsRepository.useAttendMentorshipMutation()

	async function execute(body: { mentorshipId: string }) {
		try {
			await _attendMentorship(body)
		} catch (error) {
			console.error(error)
		}
	}

	return { execute, isLoading, isSuccess }
}

function useFeedbackMutation() {
	const [_feedbackMentorship, { isLoading, isSuccess }] =
		mentorshipsRepository.useFeedbackMentorshipMutation()

	async function execute(body: {
		mentorshipId: string
		feedback: string | null
		rating: number | null
		reason: string | null | undefined
		reasonOther: string | null | undefined
		happened: boolean
		type: string
	}) {
		try {
			await _feedbackMentorship(body)
		} catch (error) {
			console.error(error)
		}
	}

	return { execute, isLoading, isSuccess }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
	list: useListMentorships,
	listMutation: useListMentorshipsMutation,
	createMentorship: useCreateMutation,
	rescheduleMentorship: useRescheduleMutation,
	cancelMentorship: useCancelMutation,
	attendMentorship: useAttendMutation,
	feedbackMentorship: useFeedbackMutation,
}
